import { EditContent, GetContentDetail, GetContentList, GetTemList, PatchEditTem, PostCepingContent } from "@/request/common";
import { getCurrentTime } from "@/utils/base";

const ZHUANBING_RELATED_TYPE = "四诊客观化数据";
const ZHUANBING_TEMPLATE_NAME = "专病";
const ZHUANBING_REPORT_TYPE = {
  id: 697,
  label: "河南四诊报告",
  value: "henan.sizhen.cal_report",
};

type VmLike = any;
const normalizeCheckId = (checkId?: string | number) => (checkId === undefined || checkId === null ? "" : String(checkId));
const getCacheKey = (patientId: number, checkId?: string | number) => {
  const checkSeg = normalizeCheckId(checkId) || "default";
  return `zhuanbing_content_id_${patientId}_${checkSeg}`;
};

function buildLoader(vm: VmLike, text: string) {
  return vm.$loading({
    lock: true,
    text,
    spinner: "el-icon-loading",
    customClass: "loadClass",
    background: "rgba(255, 255, 255, 0.7)",
  });
}

function ensureTemplateForZhuanbing(template: any) {
  const next = { ...(template || {}) };
  next.name = ZHUANBING_TEMPLATE_NAME;
  if (
    !next.report_type ||
    !next.report_type.id ||
    !next.report_type.label ||
    !next.report_type.value
  ) {
    next.report_type = { ...ZHUANBING_REPORT_TYPE };
  }
  return next;
}

async function ensureTemplateReportTypePersisted(vm: VmLike, template: any): Promise<any> {
  if (!template || !template.id) return ensureTemplateForZhuanbing(template);
  const fixed = ensureTemplateForZhuanbing(template);
  const origin = template.report_type || {};
  const needPatch =
    origin.id !== fixed.report_type.id ||
    origin.label !== fixed.report_type.label ||
    origin.value !== fixed.report_type.value ||
    template.name !== ZHUANBING_TEMPLATE_NAME;
  if (!needPatch) return fixed;
  await PatchEditTem(vm, template.id, {
    name: ZHUANBING_TEMPLATE_NAME,
    report_type: fixed.report_type,
  });
  return fixed;
}

function isZhuanbingRecord(item: any): boolean {
  return !!(item && item.template && item.template.name === ZHUANBING_TEMPLATE_NAME);
}

function getRecordCheckId(item: any): string {
  return normalizeCheckId(item?.report?.zhuanbing?.checkId);
}

function isMatchCheck(item: any, checkId?: string | number): boolean {
  const target = normalizeCheckId(checkId);
  if (!target) return true;
  return getRecordCheckId(item) === target;
}

function getZhuanbingFromList(data: any[], checkId?: string | number): any | null {
  if (!Array.isArray(data) || data.length === 0) return null;
  const list = data
    .filter((item: any) => isZhuanbingRecord(item) && isMatchCheck(item, checkId))
    .sort((a: any, b: any) => {
      const ta = new Date(a.collect_time || a.create_datetime || 0).getTime();
      const tb = new Date(b.collect_time || b.create_datetime || 0).getTime();
      return tb - ta;
    });
  return list[0] || null;
}

export async function getZhuanbingContent(vm: VmLike, patientId: number, checkId?: string | number): Promise<any | null> {
  const cacheKey = getCacheKey(patientId, checkId);
  const cachedId = sessionStorage.getItem(cacheKey);
  if (cachedId) {
    try {
      const detail = await GetContentDetail(vm, cachedId);
      const item = detail?.data;
      if (isZhuanbingRecord(item) && isMatchCheck(item, checkId)) {
        if (item.template) {
          item.template = ensureTemplateForZhuanbing(item.template);
        }
        return item;
      }
      sessionStorage.removeItem(cacheKey);
    } catch (e) {
      sessionStorage.removeItem(cacheKey);
    }
  }

  const params: any = {
    params: {
      page: 1,
      limit: 200,
      patient_id: patientId,
      related_type: ZHUANBING_RELATED_TYPE,
      has_related_type: true,
      template_name: ZHUANBING_TEMPLATE_NAME,
    },
  };
  const res = await GetContentList(vm, params);
  const raw = res?.data;
  const list = Array.isArray(raw) ? raw : raw?.results || [];
  const found = getZhuanbingFromList(list, checkId);
  if (found && found.id) {
    sessionStorage.setItem(cacheKey, String(found.id));
    if (found.template) {
      found.template = ensureTemplateForZhuanbing(found.template);
    }
  }
  return found;
}

async function queryTemplate(vm: VmLike, query: any) {
  const res = await GetTemList(vm, {
    params: {
      page: 1,
      limit: 200,
      ignore_permissions: "true",
      ...query,
    },
  });
  return res?.data || [];
}

async function findZhuanbingTemplate(vm: VmLike): Promise<any | null> {
  let list = await queryTemplate(vm, { name: ZHUANBING_TEMPLATE_NAME });
  if (list.length > 0) return list[0];
  list = await queryTemplate(vm, { name__icontains: ZHUANBING_TEMPLATE_NAME });
  if (list.length > 0) {
    const exact = list.find((t: any) => t && t.name === ZHUANBING_TEMPLATE_NAME);
    return exact || list[0];
  }
  return null;
}

export async function ensureZhuanbingContent(
  vm: VmLike,
  patientId: number,
  diseaseName: string,
  diseaseKey: string,
  checkId?: string | number
): Promise<any> {
  const existed = await getZhuanbingContent(vm, patientId, checkId);
  if (existed && existed.id) {
    return existed;
  }

  const template = await findZhuanbingTemplate(vm);
  if (!template) {
    throw new Error("未找到“专病”模板，请先在后台创建模板");
  }
  const fixedTemplate = await ensureTemplateReportTypePersisted(vm, template);

  const initReport = {
    zhuanbing: {
      diseaseName,
      diseaseKey,
      checkId: normalizeCheckId(checkId),
      modules: {},
      updatedAt: getCurrentTime("yyyy-MM-dd HH:mm:ss"),
    },
  };
  const createRes = await PostCepingContent(vm, {
    collect_time: getCurrentTime("yyyy-MM-dd HH:mm:ss"),
    patient: patientId,
    template: fixedTemplate,
    data: fixedTemplate.fields || template.fields || [],
    related_type: ZHUANBING_RELATED_TYPE,
    report: initReport,
  });
  const created = createRes?.data;
  if (created && created.id) {
    sessionStorage.setItem(getCacheKey(patientId, checkId), String(created.id));
  }
  return created;
}

export async function upsertZhuanbingModule(
  vm: VmLike,
  params: {
    patientId: number;
    diseaseName: string;
    diseaseKey: string;
    checkId?: string | number;
    moduleKey: "scale" | "bianzheng" | "zhenghou";
    moduleData: any;
    loadingText?: string;
  }
): Promise<any> {
  const loading = buildLoader(vm, params.loadingText || "保存中...");
  try {
    const content = await ensureZhuanbingContent(vm, params.patientId, params.diseaseName, params.diseaseKey, params.checkId);
    if (!isZhuanbingRecord(content)) {
      throw new Error("当前记录不是“专病”模板，已阻止写入以避免影响其他报告");
    }

    const nextReport: any = { ...(content.report || {}) };
    const zhuanbingRoot: any = { ...(nextReport.zhuanbing || {}) };
    const modules: any = { ...(zhuanbingRoot.modules || {}) };
    modules[params.moduleKey] = params.moduleData;
    zhuanbingRoot.diseaseName = params.diseaseName || zhuanbingRoot.diseaseName || "";
    zhuanbingRoot.diseaseKey = params.diseaseKey || zhuanbingRoot.diseaseKey || "";
    zhuanbingRoot.checkId = normalizeCheckId(params.checkId) || zhuanbingRoot.checkId || "";
    zhuanbingRoot.modules = modules;
    zhuanbingRoot.updatedAt = getCurrentTime("yyyy-MM-dd HH:mm:ss");
    nextReport.zhuanbing = zhuanbingRoot;

    const patchPayload = {
      collect_time: content.collect_time || getCurrentTime("yyyy-MM-dd HH:mm:ss"),
      patient: params.patientId,
      template: await ensureTemplateReportTypePersisted(vm, content.template),
      data: content.data || [],
      related_type: ZHUANBING_RELATED_TYPE,
      report: nextReport,
    };
    const editRes = await EditContent(vm, content.id, patchPayload, loading);
    if (content?.id) {
      sessionStorage.setItem(getCacheKey(params.patientId, params.checkId), String(content.id));
    }
    return editRes?.data;
  } finally {
    loading.close();
  }
}

export { ZHUANBING_RELATED_TYPE, ZHUANBING_TEMPLATE_NAME };
